import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { autoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import { NOTO_SANS_URL } from './shared/constants/fonts-url.constant';
import { LogoutPageComponent } from './shared/logout-page/logout-page.component';
import { UnauthorizedComponent } from './shared/unauthorized/unauthorized.component';
import { procedureConfigCanMatch } from './shared/guards/procedure-config.guard';
import { PROCEDURE_CONFIG_NAMES } from './service/procedure-config.constants';

const routes: Routes = [
  {
    path: 'etax',
    loadChildren: () => import('./etax/etax.module').then(m => m.EtaxModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      enableActivityTracking: true
    }
  },
  {
    path: 'cws-registration',
    loadChildren: () => import('./simplified-cws-registration/simplified-cws-registration.module').then(m => m.SimplifiedCwsRegistrationModule)
  },
  {
    path: 'ereport',
    loadChildren: () => import('./ereport/fra/fra.module').then(m => m.FraModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      enableActivityTracking: true
    }
  },
  {
    path: 'gasa-fund',
    loadChildren: () => import('./gasa-fund-enhancement/gasa-fund-enhancement.module').then(m => m.GasaFundEnhancementModule),
    data: {
      enableActivityTracking: true
    },
    canActivate: [autoLoginPartialRoutesGuard],
  },
  {
    path: 'family-registration',
    loadChildren: () => import('./registration/registration.module').then(m => m.RegistrationModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      enableActivityTracking: true
    }
  },
  {
    path: 'stop-mailing-certificates',
    loadChildren: () => import('./stop-mailing-certificates/stop-mailing-certificates.module').then(m => m.StopMailingCertificatesModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      enableActivityTracking: true
    }
  },
  {
    path: 'mobile-registration',
    loadChildren: () => import('./mobile-registration/mobile-registration.module').then(m => m.MobileRegistrationModule),
    data: {
      enableActivityTracking: true
    }
  },
  {
    path: 'policy-download',
    loadChildren: () => import('./policy-download/policy-download.module').then(m => m.PolicyDownloadModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'rp-surrender',
    loadChildren: () => import('./rp-surrender/rp-surrender.module').then(m => m.RpSurrenderModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      enableActivityTracking: true
    },
  },
  {
    path: 'stp-cws-registration',
    loadChildren: () => import('./stp-cws-registration/stp-cws-registration.module').then(m => m.StpCwsRegistrationModule),
    // canActivate: [autoLoginPartialRoutesGuard], // STP registration flow is accessible without login, as per the requirement. Once the user completes the registration then only we will authenticate the user and fetch the application status.
    data: {
      cwsFonts: [NOTO_SANS_URL]
    }
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent
  },
  {
    path: 'forbidden',
    component: UnauthorizedComponent
  },
  {
    path: 'credit-card',
    loadChildren: () => import('./cws2.0/credit-card/credit-card.module').then(m => m.CreditCardModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'ff-drawer',
    loadChildren: () => import('./cws2.0/ff-drawer/ff-drawer.module').then(m => m.FfDrawerModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'contract-loan',
    loadChildren: () => import('./cws2.0/contract-loan/contract-loan.module').then(m => m.ContractLoanModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'securities-reissue',
    loadChildren: () => import('./cws2.0/securities-reissue/securities-reissue.module').then(m => m.SecuritiesReissueModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'fixed-premium-change',
    loadChildren: () => import('./cws2.0/fixed-premium-change/fixed-premium-change.module').then(m => m.FixedPremiumChangeModule),
    canActivate: [autoLoginPartialRoutesGuard],
    data: {
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'bank-account-change',
    loadChildren: () => import('./cws2.0/bank-account-change/bank-account-change.module').then(m => m.BankAccountChangeModule),
    canActivate: [autoLoginPartialRoutesGuard],
    canMatch: [procedureConfigCanMatch],
    data: {
      procedureConfigName: PROCEDURE_CONFIG_NAMES.BANK_ACCOUNT_CHANGE,
      sfdcTarget: 'bankAccountChangePrecautions',
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
     path: 'first-name-change',
    loadChildren: () => import('./cws2.0/first-name-change/first-name-change.module').then(m => m.FirstNameChangeModule),
    canActivate: [autoLoginPartialRoutesGuard],
    canMatch: [procedureConfigCanMatch],
    data: {
      procedureConfigName: PROCEDURE_CONFIG_NAMES.OWNER_NAME_CHANGE,
      sfdcTarget: 'RightHolderNameChange',
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'family-name-change',
    loadChildren: () => import('./cws2.0/family-name-change-procedure/family-name-change-procedure.module').then(m => m.FamilyNameChangeProcedureModule),
    canActivate: [autoLoginPartialRoutesGuard],
    canMatch: [procedureConfigCanMatch],
    data: {
      procedureConfigName: PROCEDURE_CONFIG_NAMES.FAMILY_NAME_CHANGE,
      sfdcTarget: 'FamilyNameChange',
      sfdcUseHomeNavigation: true,
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'address-telephone-change',
    loadChildren: () => import('./cws2.0/address-telephone-change/address-telephone-change.module').then(m => m.AddressTelephoneChangeModule),
    canActivate: [autoLoginPartialRoutesGuard],
    canMatch: [procedureConfigCanMatch],
    data: {
      procedureConfigName: PROCEDURE_CONFIG_NAMES.ADDRESS_TEL_CHANGE,
      sfdcTarget: 'addresschange',
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'tax-deduction-certificate',
    loadChildren: () => import('./cws2.0/tax-deduction-certificate/tax-deduction-certificate.module').then(m => m.TaxDeductionCertificateModule),
    canActivate: [autoLoginPartialRoutesGuard],
    canMatch: [procedureConfigCanMatch],
    data: {
      procedureConfigName:
        PROCEDURE_CONFIG_NAMES.INSURANCE_PREMIUM_DEDUCTION_CERTIFICATE,
      sfdcTarget: 'taxreissue',
      sfdcUseHomeNavigation: true,
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'surrender-request-form-mailing',
    loadChildren: () => import('./cws2.0/surrender-request-form-mailing/surrender-request-form-mailing.module').then(m => m.SurrenderRequestFormMailingModule),
    canActivate: [autoLoginPartialRoutesGuard],
    canMatch: [procedureConfigCanMatch],
    data: {
      procedureConfigName:
        PROCEDURE_CONFIG_NAMES.SURRENDER_REQUEST_FORM_MAILING,
      sfdcTarget: 'TDSurrender_1',
      sfdcIncludeCws: true,
      cwsFonts: [NOTO_SANS_URL],
      enableActivityTracking: true
    }
  },
  {
    path: 'cws-user-id-change',
    loadChildren: () => import('./user-id-change/user-id-change.module').then(m => m.UserIdChangeModule),
    data: {
      cwsFonts: [NOTO_SANS_URL]
    }
  },
  {
    path: 'cws-logout',
    component: LogoutPageComponent
  },
  {
    path: '', redirectTo: 'etax', pathMatch: 'full'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
